/**
 * Single-tone notification "ding" synthesised via Web Audio API.
 *
 * Avoids shipping an audio asset (smaller bundle, no extra HTTP request,
 * no licensing concerns). Browsers block playback until the visitor
 * interacts with the page, so the first call may silently no-op.
 */
let ctx: AudioContext | null = null;
let lastPlay = 0;

export function playNotification(): void {
  // Throttle: at most one ding per 800ms — prevents spam when several
  // messages land at once (e.g. an agent paste).
  const now = Date.now();
  if (now - lastPlay < 800) return;
  lastPlay = now;

  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;

    ctx = ctx ?? new Ctx();
    if (ctx.state === "suspended") void ctx.resume();

    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, t);
    osc.frequency.exponentialRampToValueAtTime(660, t + 0.12);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.18, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
    osc.start(t);
    osc.stop(t + 0.26);
  } catch {
    // Autoplay blocked or Web Audio unavailable — fail silently.
  }
}
